import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import useFormValidations from "@/app/hooks/useFormValidations";
import Input from "@/app/components/ui/Inputs/Input";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { useSampleRequestAPI } from "@/app/hooks/api_hooks/LabReport/useSampleRequestAPI";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";

import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useNavigate } from "react-router";
import { Truck, Info } from "lucide-react";
import clsx from "clsx";

export interface ISampleRequest {
  lab_partner: string;
  test_barcode: string;
}

interface IUploadLabReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requisitionId: string | null;
  isDayClosing?: boolean;
  testDetails?: any;
  invoiceId?: string;
}

const SamplePickupRequestModal: React.FC<IUploadLabReportModalProps> = ({
  open,
  setOpen,
  requisitionId,
  isDayClosing,
  testDetails,
  invoiceId,
}) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  const { forOnlyBarcode } = useFormValidations();
  const { execute: samplePickUpRequest } = useSampleRequestAPI();

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const [loader, setLoader] = useState(false);

  const isLabTestUrl = location.pathname.includes("lab-test");

  const formHook = useForm<ISampleRequest>({
    mode: "onChange",
    defaultValues: {
      test_barcode: "",
      lab_partner: "",
    },
  });

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const onSubmit = (data: ISampleRequest) => {
    setLoader(true);
    samplePickUpRequest(requisitionId, "handover").then(({ status }) => {
      setLoader(false);
      if (status) {
        setOpen(false);
        processId &&
          navigate(
            `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
          );
        if (processId) {
          // Day closing: the navigate above bumps `t`, which makes the closing tab
          // refetch its own list. Fetching here too would overwrite it.
        } else if (isLabTestUrl) {
          const constructedBody = {
            start_date: labReportListFilter?.start_date,
            end_date: labReportListFilter?.end_date,
            customer_id: customerId,
            type: 3,
          };
          // fetchStatsList(constructedBody);
          fetchLabReports(constructedBody);
        } else {
          const payload: any = {
            start_date: labReportListFilter?.start_date,
            end_date: labReportListFilter?.end_date,
            status_ids: labReportListFilter?.status_ids,
            clinic_ids: labReportListFilter?.clinic_ids,
            lab_partner_ids: labReportListFilter?.lab_partner_ids,
            type: 1,
          };
          fetchStatsList(payload);
          fetchLabReports(payload);
        }
        formHook.reset();
      }
    });
  };

  const watchedBarcode = formHook?.watch("test_barcode");
  const isBarcodeMatched = watchedBarcode && testDetails?.trf_code && watchedBarcode?.trim() === testDetails?.trf_code;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => {
          setOpen(false);
          formHook.reset();
        }}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <form
              onSubmit={formHook.handleSubmit(onSubmit)}
              className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-xl"
            >
              <div className="bg-[#F9FAFB] px-6 py-4 flex items-center justify-between border-b border-gray-100 sticky top-0 z-10">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-black rounded-xl">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 leading-tight">Request Sample Pickup</h2>
                    <p className="text-[11px] text-gray-500 font-medium  tracking-wider mt-0.5">
                      Requisition Logistics
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-full hover:bg-gray-200 transition-colors bg-gray-100"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  <XMarkIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 font-primary">
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Requisition ID</p>
                    <p className="text-xs font-medium text-gray-900">{testDetails?.requisition_number || "NA"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Customer Name</p>
                    <p className="text-xs font-medium text-gray-900">{testDetails?.customer_details?.name || "NA"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Pet Details</p>
                    <p className="text-xs font-medium text-gray-900">
                      {testDetails?.pet_details?.name || "NA"}
                      {testDetails?.pet_details?.pet_species_name && (
                        <span className="text-gray-400 font-normal ml-1">
                          ({testDetails?.pet_details?.pet_species_name?.toLowerCase() === "canine" ? "Dog" : "Cat"})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500  tracking-wider">Lab Partner</p>
                    <p
                      className="text-xs font-medium text-gray-900 line-clamp-1"
                      title={testDetails?.diagnostic_lab_name || testDetails?.lab_partner_name}
                    >
                      {testDetails?.diagnostic_lab_name || testDetails?.lab_partner_name || "NA"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500">Doctor</p>
                    <p
                      className="text-xs font-medium text-gray-900"
                      title={testDetails?.staff_details?.name}
                    >
                      {testDetails?.staff_details?.name || "NA"}
                    </p>
                  </div>

                  <div className="space-y-1 col-span-2">
                    <p className="text-[10px] font-semibold text-gray-500">Test Name</p>
                    <p
                      className="text-xs text-gray-900"
                      title={testDetails?.test_name || testDetails?.lab_report_name}
                    >
                      {testDetails?.test_name || testDetails?.lab_report_name || "NA"}
                    </p>
                    {testDetails?.lab_tests?.length > 0 && (
                      <ol className="list-decimal list-inside mt-1 space-y-0.5 text-[11px] text-gray-700">
                        {testDetails.lab_tests.map((test, index) => (
                          <li key={index}>{test}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5 ml-1">
                      TRF Verification <span className="text-red-500">*</span>
                    </label>
                    <Input
                      className="text-sm rounded-xl border-gray-200 focus:ring-black focus:border-black py-2"
                      inputClassName="!h-9"
                      placeholder="Scan or enter barcode"
                      register={formHook.register("test_barcode", {
                        required: true,
                      })}
                      fieldError={formHook?.formState?.errors?.test_barcode}
                      errorMessages={[{ message: "Barcode is required", type: "required" }, forOnlyBarcode.errors]}
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-2.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loader || !isBarcodeMatched}
                  className={clsx(
                    "flex-1 px-10 py-2.5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95",
                    {
                      "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none": loader || !isBarcodeMatched,
                      "bg-black text-white hover:bg-gray-900": !loader && isBarcodeMatched,
                    },
                  )}
                >
                  {loader ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Requesting...</span>
                    </div>
                  ) : (
                    "Send to Lab"
                  )}
                </button>
              </div>
            </form>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SamplePickupRequestModal;
